import axios from 'axios';

const rawBackendUrl = (process.env.REACT_APP_BACKEND_URL || '').trim().replace(/\/$/, '');
const BACKEND_URL = rawBackendUrl.replace(/\/api$/, '');
const API = `${BACKEND_URL}/api`;

const MAX_IMPORT_BYTES = 2 * 1024 * 1024;

function authHeaders(token) {
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export function sanitizeWorkflowDownloadFilename(name) {
  const slug = String(name || 'workflow')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 80);
  return `${slug || 'workflow'}.fundelite.json`;
}

export function parseContentDispositionFilename(headerValue, fallbackName) {
  const header = String(headerValue || '');
  const match = header.match(/filename\*?=(?:UTF-8''|")?([^\";]+)"?/i);
  if (match?.[1]) {
    try {
      return decodeURIComponent(match[1].replace(/"/g, '').trim());
    } catch {
      return match[1].replace(/"/g, '').trim();
    }
  }
  return sanitizeWorkflowDownloadFilename(fallbackName);
}

export function isSupportedWorkflowImportFilename(filename) {
  const lower = String(filename || '').toLowerCase();
  return lower.endsWith('.fundelite.json') || lower.endsWith('.json');
}

export function validateHttpsImportUrl(url) {
  const raw = String(url || '').trim();
  if (!raw) return { ok: false, error: 'URL is required' };
  let parsed;
  try {
    parsed = new URL(raw);
  } catch {
    return { ok: false, error: 'URL must use HTTPS' };
  }
  if (parsed.protocol !== 'https:') {
    return { ok: false, error: 'URL must use HTTPS' };
  }
  const host = (parsed.hostname || '').toLowerCase();
  if (!host || host === 'localhost' || host === '127.0.0.1' || host === '::1') {
    return { ok: false, error: 'URL points to a private or internal network' };
  }
  return { ok: true, url: raw };
}

export function safeImportErrorMessage(err, fallback = 'Unable to import workflow') {
  const detail = err?.response?.data?.detail;
  if (typeof detail === 'string' && detail.trim()) return detail;
  if (detail && typeof detail === 'object') {
    const first = Array.isArray(detail.errors) ? detail.errors[0] : null;
    if (typeof first === 'string' && first.trim()) return first;
    if (typeof detail.message === 'string' && detail.message.trim()) return detail.message;
  }
  if (typeof err?.message === 'string' && !/api_key|password|token|secret/i.test(err.message)) {
    return err.message;
  }
  return fallback;
}

export async function readWorkflowImportFile(file) {
  if (!file) {
    throw new Error('Invalid JSON file');
  }
  if (!isSupportedWorkflowImportFilename(file.name)) {
    throw new Error('Unsupported FundElite workflow format');
  }
  if (file.size <= 0) {
    throw new Error('Invalid JSON file');
  }
  if (file.size > MAX_IMPORT_BYTES) {
    throw new Error('File exceeds the 2 MB limit');
  }
  let text = '';
  try {
    if (typeof file.text === 'function') {
      text = await file.text();
    } else {
      text = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result || ''));
        reader.onerror = () => reject(new Error('Invalid JSON file'));
        reader.readAsText(file);
      });
    }
  } catch {
    throw new Error('Invalid JSON file');
  }
  if (!String(text || '').trim()) {
    throw new Error('Invalid JSON file');
  }
  try {
    return JSON.parse(text);
  } catch {
    throw new Error('Invalid JSON file');
  }
}

export async function downloadPlatformWorkflow(token, workflowId, workflowName) {
  const res = await axios.get(
    `${API}/platform/system-automations/workflows/${workflowId}/export`,
    {
      headers: authHeaders(token),
      responseType: 'blob',
    },
  );
  const filename = parseContentDispositionFilename(
    res.headers?.['content-disposition'],
    workflowName,
  );
  return { blob: res.data, filename, endpoint: `/platform/system-automations/workflows/${workflowId}/export` };
}

export function triggerBrowserDownload(blob, filename) {
  const url = window.URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.URL.revokeObjectURL(url);
}

export async function previewPlatformWorkflowImport(token, packageJson) {
  const res = await axios.post(
    `${API}/platform/system-automations/workflows/import/preview`,
    { package: packageJson },
    { headers: authHeaders(token) },
  );
  return res.data;
}

export async function importPlatformWorkflowFile(token, packageJson) {
  const res = await axios.post(
    `${API}/platform/system-automations/workflows/import/file`,
    { package: packageJson },
    { headers: authHeaders(token) },
  );
  return res.data;
}

export async function previewPlatformWorkflowImportUrl(token, url) {
  const res = await axios.post(
    `${API}/platform/system-automations/workflows/import/url/preview`,
    { url },
    { headers: authHeaders(token) },
  );
  return res.data;
}

export async function importPlatformWorkflowUrl(token, url) {
  const res = await axios.post(
    `${API}/platform/system-automations/workflows/import/url`,
    { url },
    { headers: authHeaders(token) },
  );
  return res.data;
}

export async function downloadAgencyWorkflow(token, agencyId, workflowId, workflowName) {
  const res = await axios.get(
    `${API}/workflows/${agencyId}/${workflowId}/export`,
    {
      headers: authHeaders(token),
      responseType: 'blob',
    },
  );
  const filename = parseContentDispositionFilename(
    res.headers?.['content-disposition'],
    workflowName,
  );
  return { blob: res.data, filename };
}

export async function previewAgencyWorkflowImport(token, agencyId, packageJson) {
  const res = await axios.post(
    `${API}/workflows/${agencyId}/import/preview`,
    { package: packageJson },
    { headers: authHeaders(token) },
  );
  return res.data;
}

export async function importAgencyWorkflowFile(token, agencyId, packageJson) {
  const res = await axios.post(
    `${API}/workflows/${agencyId}/import/file`,
    { package: packageJson },
    { headers: authHeaders(token) },
  );
  return res.data;
}

export async function previewAgencyWorkflowUrl(token, agencyId, url) {
  const res = await axios.post(
    `${API}/workflows/${agencyId}/import/url/preview`,
    { url },
    { headers: authHeaders(token) },
  );
  return res.data;
}

export async function importAgencyWorkflowUrl(token, agencyId, url) {
  const res = await axios.post(
    `${API}/workflows/${agencyId}/import/url`,
    { url },
    { headers: authHeaders(token) },
  );
  return res.data;
}

/** Alias matching platform naming for shared callers. */
export const previewAgencyWorkflowImportUrl = previewAgencyWorkflowUrl;

/** Shared safe-error helper alias used by builder download handlers. */
export const getSafeWorkflowPortableError = safeImportErrorMessage;
